<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { definePage } from "unplugin-vue-router/runtime";
import { computed } from "vue";
import { useAbility } from "@/api/models/auth/ability";
import { useMsoPlan } from "@/composables/models/billing/usePracticePlan";
import { useMsoConnectionCount } from "@/composables/models/practice/useConnectedPractices";
import {
  canOpenBillingPage,
  canScopeOpenBillingPage,
  readBillingRights,
  type TBillingScope,
} from "@/composables/shared/billingAccess";
import { useBillingOwner } from "@/composables/shared/useBillingOwner";
import { useAccountRep } from "@/composables/shared/useAccountRep";
import { formatCurrency } from "@/utils/formatCurrency";
import { ESubscriptionProductCurrency } from "@/api/models/billing/subscriptionProduct";
import MsoPlanTrialBanner from "@/components/models/practice/detail/MsoPlanTrialBanner.vue";
import BalanceCard from "@/components/models/billing/BalanceCard.vue";
import RecentPayoutsCard from "@/components/models/billing/RecentPayoutsCard.vue";
import RecentInvoicesCard from "@/components/models/billing/RecentInvoicesCard.vue";
import RecentTransactionsCard from "@/components/models/billing/RecentTransactionsCard.vue";
import PracticePlanWidgetsSection from "@/components/models/company/detail/PracticePlanWidgetsSection.vue";
import BillingShell from "@/components/models/billing/BillingShell.vue";
import CouponCodeCard from "@/components/models/billing/CouponCodeCard.vue";
import SectionCard from "@/components/ui/cards/SectionCard.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";

definePage({
  meta: {
    title: "generic.fields.sections.general",
    checkPrivileges: () => {
      const { can } = useAbility();
      return canOpenBillingPage(readBillingRights(can), "general");
    },
  },
});

const { t } = useI18n();
const { can } = useAbility();
const { scope, activeMso } = useBillingOwner();
const { contactSupport } = useAccountRep();

const msoId = computed(() => (scope.value === "mso" ? activeMso.value?.id : undefined));
const hasMso = computed(() => !!msoId.value);

const canViewSubscription = computed(
  () =>
    can($permActions.MANAGE_MSO_SUBSCRIPTION, $permSubjects.BILLING__MSOSUBSCRIPTION) ||
    can($permActions.MANAGE_MSO_SUBSCRIPTION_ADMIN, $permSubjects.BILLING__MSOSUBSCRIPTION)
);
const { subscription, currentProduct, isTrialActive } = useMsoPlan(
  msoId,
  computed(() => canViewSubscription.value && hasMso.value)
);

const { usedConnections } = useMsoConnectionCount(msoId, hasMso);

const poolLimit = computed(() => currentProduct.value?.conections ?? 0);

const canSeeInvoiceHistory = (ownerScope: TBillingScope) =>
  canScopeOpenBillingPage(readBillingRights(can), ownerScope, "invoices");
</script>

<template>
  <billing-shell page="general">
    <template #default="{ scope: ownerScope, mso, company, canManageBilling }">
      <div
        v-if="ownerScope === 'mso' && mso"
        class="tw:flex tw:flex-col tw:gap-5"
      >
        <mso-plan-trial-banner :mso-id="mso.id" />

        <section-card
          :title="t('models.mso.representative.currentPlanStat')"
          icon="mdi-crown-outline"
          class="tw:flex tw:flex-col"
          body-class="tw:flex-1 tw:flex tw:flex-col"
        >
          <template #actions>
            <primary-button
              size="small"
              @click="contactSupport"
            >
              {{ t("models.mso.requestPlanUpgrade") }}
            </primary-button>
          </template>

          <div
            class="tw:rounded-lg tw:border tw:border-on-surface/10 tw:p-4.5! tw:flex tw:flex-1 tw:flex-col tw:justify-center tw:gap-5"
          >
            <div class="tw:flex tw:flex-col tw:gap-2">
              <div class="tw:flex tw:items-center tw:gap-2.5 tw:flex-wrap">
                <span class="tw:text-[17px] tw:font-extrabold">
                  {{ currentProduct?.name ?? t("generic.status.none") }}
                </span>
                <v-chip
                  v-if="subscription"
                  size="x-small"
                  variant="tonal"
                  :color="subscription.is_active ? 'success' : 'default'"
                >
                  {{
                    subscription.is_active
                      ? t("generic.fields.statusOptions.active")
                      : t("generic.fields.statusOptions.notActive")
                  }}
                </v-chip>
                <v-chip
                  v-if="isTrialActive"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                >
                  {{ t("models.settings.subscriptionProducts.history.trial") }}
                </v-chip>
              </div>

              <div class="tw:text-[13px] tw:text-on-surface/55">
                {{ currentProduct?.description || mso.name }}
              </div>
            </div>

            <div class="tw:flex tw:flex-wrap tw:gap-7">
              <div>
                <div
                  class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.04em] tw:text-on-surface/45"
                >
                  {{ t("models.mso.monthlyInvoice") }}
                </div>
                <div class="tw:text-[19px] tw:font-extrabold tw:text-secondary tw:mt-0.5!">
                  {{
                    t("features.practicePlan.widget.pricePerMonth", {
                      price: formatCurrency(
                        Number(currentProduct?.price ?? 0),
                        currentProduct?.currency ?? ESubscriptionProductCurrency.USD
                      ),
                    })
                  }}
                </div>
              </div>
              <div>
                <div
                  class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.04em] tw:text-on-surface/45"
                >
                  {{ t("models.mso.connections") }}
                </div>
                <div class="tw:text-[19px] tw:font-extrabold tw:mt-0.5!">
                  <template v-if="usedConnections !== null">
                    {{ usedConnections }} / {{ poolLimit }}
                  </template>
                  <template v-else>
                    {{ poolLimit }}
                  </template>
                </div>
              </div>
            </div>
          </div>
        </section-card>

        <div class="tw:grid tw:grid-cols-1 tw:gap-5 tw:lg:grid-cols-2 tw:lg:items-stretch">
          <balance-card
            scope="mso"
            :owner-id="mso.id"
            :can-manage="canManageBilling"
          />
          <recent-payouts-card
            scope="mso"
            :owner-id="mso.id"
          />
          <template v-if="canSeeInvoiceHistory('mso')">
            <recent-invoices-card
              scope="mso"
              :owner-id="mso.id"
            />
            <recent-transactions-card
              scope="mso"
              :owner-id="mso.id"
            />
          </template>
        </div>

        <coupon-code-card />
      </div>

      <div
        v-else-if="ownerScope === 'company' && company"
        :key="`company-${company.id}`"
        class="tw:flex tw:flex-col tw:gap-5"
      >
        <practice-plan-widgets-section :company="company" />

        <div class="tw:grid tw:grid-cols-1 tw:gap-5 tw:lg:grid-cols-2 tw:lg:items-stretch">
          <balance-card
            scope="company"
            :owner-id="company.id"
            :can-manage="canManageBilling"
          />
          <recent-payouts-card
            scope="company"
            :owner-id="company.id"
          />
          <template v-if="canSeeInvoiceHistory('company')">
            <recent-invoices-card
              scope="company"
              :owner-id="company.id"
            />
            <recent-transactions-card
              scope="company"
              :owner-id="company.id"
            />
          </template>
        </div>

        <coupon-code-card />
      </div>
    </template>
  </billing-shell>
</template>
